import React, { useEffect, useState } from 'react';
import { History, PlusCircle, Search, Filter, DollarSign, Eye, X, Download } from 'lucide-react';
import alertService from '../services/alertService';
import { listCaixas, fecharCaixa, getCaixaById } from '../services/caixasService';
import { criarAberturaDeCaixa } from '../services/movimentacoesCaixaService';
import { CaixaRecord } from '../types/caixa';
import { authService } from '../services/authService';
import { useNavigate } from 'react-router-dom';

const formatCurrency = (v?: number | null) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);
const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

const CaixaPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();

  const [valorAbertura, setValorAbertura] = useState<string>('0');
  const [observacoesAbertura, setObservacoesAbertura] = useState<string>('');
  const isValorAberturaValido = !Number.isNaN(Number(valorAbertura)) && Number(valorAbertura) >= 0;
  const [caixas, setCaixas] = useState<CaixaRecord[]>([]);
  const [caixaAberto, setCaixaAberto] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  
  // Filtros e paginação seguindo padrão de categorias
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusSelect, setStatusSelect] = useState<'Todos' | 'Aberto' | 'Fechado'>('Todos');
  const [sortOption, setSortOption] = useState<'Padrao' | 'AberturaAsc' | 'AberturaDesc'>('Padrao');
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [pageSize] = useState<number>(10);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Modal de detalhes do caixa
  const [selectedCaixa, setSelectedCaixa] = useState<CaixaRecord | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);

  const loadCaixas = async (opts?: { resetPage?: boolean }) => {
    setLoading(true);
    setError('');
    try {
      if (opts?.resetPage) setCurrentPage(0);
      const sortParam = sortOption === 'AberturaDesc' ? 'dataAbertura,desc' : (sortOption === 'AberturaAsc' ? 'dataAbertura,asc' : undefined);
      const pageData = await listCaixas({ 
        page: currentPage, 
        size: pageSize, 
        sort: sortParam, 
        id: searchTerm || undefined, 
        usuario: searchTerm || undefined, 
        status: statusSelect === 'Todos' ? undefined : statusSelect 
      });
      
      const registros: CaixaRecord[] = (pageData.content as any[]).map((x: any) => ({
        id: String(x.id ?? ''),
        dataAbertura: x.dataAbertura,
        // Usar o valor informado na abertura, com fallback para valorAbertura
        valorAbertura: Number((x as any).valorAberturaInformado ?? x.valorAbertura ?? 0),
        saldoAtual: Number(x.saldoAtual ?? 0),
        valorFechamentoInformado: Number(x.valorFechamentoInformado ?? 0),
        status: x.status,
        dataFechamento: x.dataFechamento,
        usuarioAberturaNome: x.usuarioAberturaNome,
        usuarioFechamentoNome: x.usuarioFechamentoNome,
        observacoes: x.observacoes,
      }));
      
      setCaixas(registros);
      setTotalPages(Math.max(1, pageData.totalPages));
      setTotalElements(pageData.totalElements);
      
      // Verifica se há caixa aberto
      const caixaAbertoExiste = registros.some(c => 
        String(c.status || '').toLowerCase().includes('berto') && !c.dataFechamento
      );
      setCaixaAberto(caixaAbertoExiste);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Falha ao carregar caixas');
      setCaixas([]);
      setCaixaAberto(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCaixas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sortOption, statusSelect]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(0);
    loadCaixas({ resetPage: true });
  };

  const handleOpenCaixa = async () => {
    try {
      setLoading(true);
      if (!isValorAberturaValido) {
        alertService.error?.('Informe um valor de abertura válido (>= 0)');
        return;
      }
      const valor = Number(valorAbertura || '0');
      const usuarioId = authService.getLoginData()?.usuarioId || authService.getUser()?.id || undefined;
      await criarAberturaDeCaixa(valor, { descricao: 'Abertura de Caixa', observacoes: observacoesAbertura, usuarioId });
      alertService.success?.('Movimentação de abertura registrada com sucesso');
      setOpenModal(false);
      setValorAbertura('0');
      setObservacoesAbertura('');
      await loadCaixas({ resetPage: true });
    } catch (e: any) {
      alertService.error?.(e?.response?.data?.message || e?.message || 'Falha ao registrar abertura de caixa');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseCaixa = async (caixaId: string) => {
    const result = await alertService.confirm?.(
      'Fechar Caixa',
      'Tem certeza que deseja fechar este caixa?',
      'Sim, fechar',
      'Cancelar'
    );
    
    if (result?.isConfirmed) {
      try {
        setLoading(true);
        await fecharCaixa(caixaId);
        alertService.success?.('Caixa fechado com sucesso!');
        loadCaixas();
      } catch (e: any) {
        alertService.error?.(e?.response?.data?.message || e?.message || 'Falha ao fechar caixa');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleViewDetails = async (caixa: CaixaRecord) => {
    try {
      setLoading(true);
      const detalhes = await getCaixaById(caixa.id);
      setSelectedCaixa({
        ...caixa,
        ...detalhes
      });
      setShowDetailsModal(true);
    } catch (e: any) {
      alertService.error?.(e?.response?.data?.message || e?.message || 'Falha ao carregar detalhes do caixa');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusSelect('Todos');
    setSortOption('Padrao');
    setCurrentPage(0);
  };

  return (
    <div>
      {/* Header seguindo padrão de categorias */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3">
          <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Caixas</h1>
            <p className="text-sm sm:text-base text-gray-600">Gerencie os caixas do sistema</p>
          </div>
        </div>
        {!caixaAberto && (
          <button
            onClick={() => setOpenModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors w-full sm:w-auto"
            disabled={loading}
          >
            <PlusCircle className="h-4 w-4" />
            <span className="sm:inline">Abrir Caixa</span>
          </button>
        )}
      </div>

      {/* Search and Filters seguindo padrão de categorias */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Buscar caixas
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Digite ID do caixa ou nome do usuário..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 sm:gap-4">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex-1 sm:flex-none px-4 py-2 border rounded-lg flex items-center justify-center gap-2 transition-colors ${
                showFilters
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="h-4 w-4" />
              <span className="hidden sm:inline">Filtros</span>
            </button>
            
            <button
              type="submit"
              className="flex-1 sm:flex-none bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
              disabled={loading}
            >
              Buscar
            </button>

            {/* Exportar Excel */}
            <button
              type="button"
              onClick={async () => {
                try {
                  alertService.loading?.('Gerando arquivo...');
                  const total = Math.max(totalElements, pageSize);
                  const sortParam = sortOption === 'AberturaDesc' ? 'dataAbertura,desc' : (sortOption === 'AberturaAsc' ? 'dataAbertura,asc' : undefined);
                  const pageData = await listCaixas({
                    page: 0,
                    size: total,
                    sort: sortParam,
                    id: searchTerm || undefined,
                    usuario: searchTerm || undefined,
                    status: statusSelect === 'Todos' ? undefined : statusSelect,
                  });
                  const registros = pageData.content || [];
                  if (registros.length === 0) {
                    alertService.close?.();
                    return alertService.info?.('Nada a exportar');
                  }
                  const sep = ';';
                  const headers = ['ID','Data Abertura','Valor Abertura','Saldo Atual','Valor Fechamento','Status','Data Fechamento','Aberto por','Fechado por','Observações'];
                  const esc = (v: any) => {
                    const s = String(v ?? '');
                    const needsQuotes = s.includes(sep) || s.includes('\n') || s.includes('"');
                    const replaced = s.replace(/"/g,'""');
                    return needsQuotes ? `"${replaced}"` : replaced;
                  };
                  const lines = [headers.join(sep), ...registros.map(c => [
                    c.id,
                    formatDate(c.dataAbertura),
                    String(c.valorAbertura ?? 0).replace('.', ','),
                    String(c.saldoAtual ?? 0).replace('.', ','),
                    String((c as any).valorFechamentoInformado ?? 0).replace('.', ','),
                    c.status ?? '',
                    formatDate(c.dataFechamento),
                    c.usuarioAberturaNome ?? '',
                    c.usuarioFechamentoNome ?? '',
                    c.observacoes ?? ''
                  ].map(esc).join(sep))];
                  const bom = '\ufeff';
                  const blob = new Blob([bom + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  const now = new Date();
                  const pad = (n: number) => n.toString().padStart(2, '0');
                  const fname = `caixas_${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}.csv`;
                  a.download = fname;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  alertService.close?.();
                } catch (e: any) {
                  alertService.close?.();
                  alertService.error?.('Falha ao exportar caixas', e?.message || 'Tente novamente');
                }
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 inline-flex items-center gap-2"
              title="Exportar para Excel"
            >
              <Download className="h-4 w-4" /> Exportar Excel
            </button>
          </div>
        </form>

        {/* Filtros expandidos */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={statusSelect}
                  onChange={(e) => setStatusSelect(e.target.value as 'Todos' | 'Aberto' | 'Fechado')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="Todos">Todos</option>
                  <option value="Aberto">Aberto</option>
                  <option value="Fechado">Fechado</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ordenação
                </label>
                <select
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value as 'Padrao' | 'AberturaAsc' | 'AberturaDesc')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="Padrao">Padrão</option>
                  <option value="AberturaAsc">Data Abertura (Crescente)</option>
                  <option value="AberturaDesc">Data Abertura (Decrescente)</option>
                </select>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={clearFilters}
                className="text-red-600 hover:text-red-700 text-sm font-medium"
              >
                Limpar filtros
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          {loading ? (
            'Carregando...'
          ) : (
            `Mostrando ${caixas.length} de ${totalElements} caixas`
          )}
        </p>
      </div>

      {/* Caixas Table seguindo padrão de categorias */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Erro ao carregar caixas</h3>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => loadCaixas({ resetPage: true })}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"
            >
              Tentar novamente
            </button>
          </div>
        ) : caixas.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum caixa encontrado</h3>
            <p className="text-gray-600 mb-4">Não há caixas cadastrados ou que correspondam aos filtros aplicados.</p>
            {!caixaAberto && (
              <button
                onClick={() => setOpenModal(true)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"
              >
                <PlusCircle className="h-4 w-4" />
                Abrir primeiro caixa
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Caixa
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor Inicial
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Saldo Atual
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor Fechamento
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fechamento
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {caixas.map((caixa) => (
                    <tr key={caixa.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <DollarSign className="h-5 w-5 text-red-600 mr-3" />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              Caixa #{caixa.id}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatDate(caixa.dataAbertura)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(caixa.valorAbertura)}
                        </div>
                        {caixa.usuarioAberturaNome ? (
                          <div className="text-sm text-gray-500">{caixa.usuarioAberturaNome}</div>
                        ) : Number(caixa.valorAbertura) > 0 ? null : (
                          <div className="text-sm text-gray-500">-</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(caixa.saldoAtual)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(caixa.valorFechamentoInformado)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          String(caixa.status).toLowerCase().includes('berto')
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {caixa.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(caixa.dataFechamento)}
                        </div>
                        {caixa.usuarioFechamentoNome && (
                          <div className="text-sm text-gray-500">
                            {caixa.usuarioFechamentoNome}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleViewDetails(caixa)}
                            className="text-blue-600 hover:text-blue-700 p-1 rounded"
                            title="Ver detalhes"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/caixas/${caixa.id}/movimentacoes`)}
                            className="text-red-600 hover:text-red-700 p-1 rounded"
                            title="Ver histórico"
                          >
                            <History className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-4">
              {caixas.map((caixa) => (
                <div key={caixa.id} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <DollarSign className="h-4 w-4 text-red-600 mr-2" />
                        <h3 className="text-sm font-medium text-gray-900">
                          Caixa #{caixa.id}
                        </h3>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        String(caixa.status).toLowerCase().includes('berto')
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {caixa.status}
                      </span>
                    </div>
                    <div className="flex gap-1 ml-2">
                      <button
                        onClick={() => handleViewDetails(caixa)}
                        className="text-blue-600 hover:text-blue-700 p-2 rounded"
                        title="Ver detalhes"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => navigate(`/caixas/${caixa.id}/movimentacoes`)}
                        className="text-red-600 hover:text-red-700 p-2 rounded"
                        title="Ver histórico"
                      >
                        <History className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-3 text-xs">
                    <div>
                      <span className="text-gray-500 block">Data Abertura:</span>
                      <span className="text-gray-900">{formatDate(caixa.dataAbertura)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Valor Inicial:</span>
                      <span className="text-gray-900">{formatCurrency(caixa.valorAbertura)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Saldo Atual:</span>
                      <span className="text-gray-900">{formatCurrency(caixa.saldoAtual)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500 block">Valor Fechamento:</span>
                      <span className="text-gray-900">{formatCurrency(caixa.valorFechamentoInformado)}</span>
                    </div>
                    {caixa.dataFechamento && (
                      <div>
                        <span className="text-gray-500 block">Data Fechamento:</span>
                        <span className="text-gray-900">{formatDate(caixa.dataFechamento)}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination seguindo padrão de categorias */}
      {totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-xs sm:text-sm text-gray-700 order-2 sm:order-1">
            Página {currentPage + 1} de {totalPages}
          </div>
          <div className="flex gap-1 sm:gap-2 order-1 sm:order-2">
            <button
              onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            
            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
              const pageNum = Math.max(0, Math.min(totalPages - 3, currentPage - 1)) + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-2 sm:px-3 py-2 border rounded-lg text-sm font-medium ${
                    currentPage === pageNum
                      ? 'bg-red-600 border-red-600 text-white'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
              disabled={currentPage === totalPages - 1}
              className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Modal de Abertura de Caixa */}
      {openModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Abrir Caixa</h3>
              <button
                onClick={() => setOpenModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valor de Abertura
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={valorAbertura}
                  onChange={(e) => setValorAbertura(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="0,00"
                />
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Observações (opcional)
                </label>
                <textarea
                  value={observacoesAbertura}
                  onChange={(e) => setObservacoesAbertura(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Digite observações sobre a abertura do caixa..."
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setOpenModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleOpenCaixa}
                  disabled={!isValorAberturaValido || loading}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Abrindo...' : 'Abrir Caixa'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Detalhes do Caixa */}
      {showDetailsModal && selectedCaixa && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Detalhes do Caixa #{selectedCaixa.id}</h3>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Informações Gerais</h4>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-500">Status:</span>
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        String(selectedCaixa.status).toLowerCase().includes('berto')
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {selectedCaixa.status}
                      </span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Data de Abertura:</span>
                      <p className="text-sm text-gray-900">{formatDate(selectedCaixa.dataAbertura)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Valor de Abertura:</span>
                      <p className="text-sm font-medium text-gray-900">{formatCurrency(selectedCaixa.valorAbertura)}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Saldo Atual:</span>
                      <p className="text-sm font-medium text-gray-900">{formatCurrency(selectedCaixa.saldoAtual)}</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Usuários</h4>
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-500">Aberto por:</span>
                      <p className="text-sm text-gray-900">{selectedCaixa.usuarioAberturaNome || '-'}</p>
                    </div>
                    {selectedCaixa.dataFechamento && (
                      <>
                        <div>
                          <span className="text-sm text-gray-500">Data de Fechamento:</span>
                          <p className="text-sm text-gray-900">{formatDate(selectedCaixa.dataFechamento)}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Fechado por:</span>
                          <p className="text-sm text-gray-900">{selectedCaixa.usuarioFechamentoNome || '-'}</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {selectedCaixa.observacoes && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Observações</h4>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">{selectedCaixa.observacoes}</p>
                </div>
              )}
              
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => navigate(`/caixas/${selectedCaixa.id}/movimentacoes`)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2"
                >
                  <History className="h-4 w-4" />
                  Ver Movimentações
                </button>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaixaPage;